import api from './api';
import { Router, ApiResponse, PaginatedResponse, CreateRouterRequest } from '../types';

class RouterService {
  async getRouters(
    page = 1,
    limit: number | 'all' = 10,
    search?: string,
    filters?: { spop_id?: string; status?: string }
  ): Promise<PaginatedResponse<Router>> {
    let url = `/routers?page=${page}&limit=${limit === 'all' ? 'all' : limit}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (filters?.spop_id) {
      url += `&spop_id=${filters.spop_id}`;
    }
    if (filters?.status) {
      url += `&status=${filters.status}`;
    }
    const response = await api.get<PaginatedResponse<Router>>(url);
    return response.data;
  }

  async getRouter(id: number): Promise<Router> {
    const response = await api.get<ApiResponse<Router>>(`/routers/${id}`);
    return response.data.data;
  }

  async createRouter(routerData: CreateRouterRequest): Promise<Router> {
    const response = await api.post<ApiResponse<Router>>('/routers', routerData);
    return response.data.data;
  }

  async updateRouter(id: string, routerData: Partial<Router>): Promise<Router> {
    const response = await api.put<ApiResponse<Router>>(`/routers/${id}`, routerData);
    return response.data.data;
  }

  async deleteRouter(id: string): Promise<void> {
    await api.delete(`/routers/${id}`);
  }

  async testConnection(connectionData: {
    host: string;
    port: number;
    username: string;
    password: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>('/routers/test-connection', connectionData);
    return response.data;
  }

  async testExistingRouter(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(`/routers/${id}/test`);
    return response.data;
  }

  async toggleRouterStatus(
    id: string,
    isActive: boolean
  ): Promise<{ success: boolean; message: string; data?: { id: string; status: string } }> {
    const response = await api.patch<{ success: boolean; message: string; data?: { id: string; status: string } }>(
      `/routers/${id}/status`,
      { isActive }
    );
    return response.data;
  }

  async pingRouter(id: string): Promise<{ success: boolean; message: string; data?: { responseTime?: number } }> {
    const response = await api.post<{ success: boolean; message: string; data?: { responseTime?: number } }>(
      `/routers/${id}/ping`
    );
    return response.data;
  }

  async getPPPProfiles(routerId: string): Promise<any[]> {
    const response = await api.get<ApiResponse<any[]>>(`/routers/${routerId}/ppp-profiles`);
    return response.data.data;
  }

  async getActiveConnections(id: number): Promise<any[]> {
    const response = await api.get<any[]>(`/routers/${id}/connections`);
    return response.data;
  }

  // Method untuk mengambil detail lengkap router dari MikroTik API
  async getRouterDetails(id: string): Promise<{
    pppSecrets: { active: number; disabled: number; total: number };
    pppConnections: { active: number; total: number };
    hotspotUsers: { total: number; active: number; disabled: number };
    systemInfo: {
      boardName: string;
      version: string;
      architecture: string;
      uptime: string;
      routerIdentity: string;
    };
    resources: {
      cpu: number;
      memory: number;
      uptime: string;
    };
    interfaces: Array<{
      name: string;
      status: string;
      rxBytes: string;
      txBytes: string;
    }>;
    traffic: {
      totalRx: string;
      totalTx: string;
      currentRx: string;
      currentTx: string;
    };
  }> {
    const response = await api.get<ApiResponse<any>>(`/routers/${id}/details`);
    return response.data.data;
  }

  // Method untuk mengambil resource usage secara live
  async getRouterResources(id: string): Promise<{
    cpu: number;
    memory: number;
    uptime: string;
  }> {
    const response = await api.get<ApiResponse<any>>(`/routers/${id}/resources`);
    return response.data.data;
  }

  // Method untuk membuat PPP Secret di MikroTik
  async createPPPSecret(routerId: string, customerData: {
    customerCode: string;
    name: string;
    username?: string;
    password?: string;
    profile?: string;
  }): Promise<{
    success: boolean;
    message: string;
    data?: {
      username: string;
      password: string;
      profile: string;
    };
  }> {
    const response = await api.post<{
      success: boolean;
      message: string;
      data?: {
        username: string;
        password: string;
        profile: string;
      };
    }>(`/routers/${routerId}/ppp-secret`, customerData);
    return response.data;
  }

  // Method untuk mengambil daftar PPP Secret yang sudah ada di MikroTik
  async getPPPSecrets(routerId: string): Promise<{
    success: boolean;
    message: string;
    data?: Array<{
      id: string;
      name: string;
      service: string;
      profile: string;
      disabled: boolean;
      comment?: string;
    }>;
  }> {
    const response = await api.get<{
      success: boolean;
      message: string;
      data?: Array<{
        id: string;
        name: string;
        service: string;
        profile: string;
        disabled: boolean;
        comment?: string;
      }>;
    }>(`/routers/${routerId}/ppp-secrets`);
    return response.data;
  }

  // Method untuk mendapatkan detail PPP Secret berdasarkan name
  async getPPPSecretByName(routerId: string, secretName: string): Promise<{
    success: boolean;
    message: string;
    data?: {
      username: string;
      password: string;
      profile: string;
      service: string;
      disabled: boolean;
    };
  }> {
    const response = await api.get<{
      success: boolean;
      message: string;
      data?: {
        username: string;
        password: string;
        profile: string;
        service: string;
        disabled: boolean;
      };
    }>(`/routers/${routerId}/ppp-secret/${encodeURIComponent(secretName)}`);
    return response.data;
  }
}

const routerService = new RouterService();
export default routerService;
